import { Controller } from 'stimulus';
import SimpleBar from 'simplebar';
import { simplebarAriaLabel } from '../javascript/lib/library_locales';

// Initialises a permanently-visible SimpleBar scrollbar on an element, but
// only when its content actually overflows horizontally. Tears it down if
// content no longer overflows (e.g. after a resize).
export default class extends Controller {
  #savedChildren = null;

  connect() {
    // SimpleBar determines scroll axes from the element's overflow CSS.
    // Set overflow-x: auto so it knows to render a horizontal scrollbar.
    this.element.style.overflowX = 'auto';

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.element);

    this.mutationObserver = new MutationObserver(() => this.scheduleUpdate());
    this.mutationObserver.observe(this.element, {
      childList: true,
      subtree: true,
    });

    // Handles the case where this element is inside a hidden ancestor (d-none)
    // at connect time — fires when the element enters the viewport.
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.update();
    });
    this.intersectionObserver.observe(this.element);

    this.update();
  }

  disconnect() {
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();
    this.intersectionObserver.disconnect();
    cancelAnimationFrame(this.rafId);
    this.#destroySimpleBar();
  }

  scheduleUpdate() {
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => this.update());
  }

  update() {
    // After SimpleBar initialises it wraps content inside its own scroll
    // element, so this.element.scrollWidth no longer reflects content width.
    // Use SimpleBar's scroll element once it exists.
    const scrollEl = this.simpleBar?.getScrollElement() ?? this.element;
    const overflows = scrollEl.scrollWidth > scrollEl.clientWidth;

    if (overflows && !this.simpleBar) {
      // Snapshot original children before SimpleBar restructures the DOM so
      // they can be restored when SimpleBar is torn down.
      this.#savedChildren = Array.from(this.element.childNodes);
      this.simpleBar = new SimpleBar(this.element, {
        autoHide: false,
        ariaLabel: simplebarAriaLabel(),
      });
    } else if (!overflows) {
      this.#destroySimpleBar();
    }
  }

  #destroySimpleBar() {
    if (!this.simpleBar) return;
    this.simpleBar.unMount();
    this.simpleBar = null;
    // SimpleBar.unMount() only removes listeners — it leaves its wrapper DOM
    // (including a placeholder with a fixed pixel width) in place. Restore the
    // original children so the element can shrink freely and overflow detection
    // keeps working on subsequent resizes.
    if (this.#savedChildren) {
      this.element.replaceChildren(...this.#savedChildren);
      this.#savedChildren = null;
    }
    // unMount() also removes our inline overflow-x style. Restore it so that
    // scrollWidth > clientWidth detection keeps working on resize.
    this.element.style.overflowX = 'auto';
  }
}
